import { useState } from 'react';

import { endOfWeek, format, startOfWeek } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { ButtonGroup, ButtonGroupText } from '@/components/ui/button-group';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatDateRange, formatDisplayDate } from '@/utils/date-formatters';

import { useViewModeAndDate } from '../hooks/use-view-mode-and-date';
import { ViewMode } from '../types/time-tracking';

export function DateNavigation() {
  const { t } = useTranslation('authenticated.timeTracking');
  const { viewMode, selectedDate, navigatePrevious, navigateNext, goToToday, setSelectedDate } =
    useViewModeAndDate();

  const [calendarOpen, setCalendarOpen] = useState(false);

  const getFormattedDate = () => {
    if (viewMode === ViewMode.Day) {
      return formatDisplayDate(selectedDate);
    }
    if (viewMode === ViewMode.Week) {
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(selectedDate, { weekStartsOn: 1 });
      return formatDateRange(weekStart, weekEnd);
    }
    // Month
    return format(selectedDate, 'MMMM yyyy');
  };

  const getTodayLabel = () => {
    if (viewMode === ViewMode.Day) return t('dateNavigation.today');
    if (viewMode === ViewMode.Week) return t('dateNavigation.thisWeek');
    return t('dateNavigation.thisMonth');
  };

  const handleCalendarSelect = (date: Date | undefined) => {
    if (!date) return;
    setSelectedDate(date);
    setCalendarOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      <ButtonGroup>
        <Button variant="outline" size="sm" onClick={navigatePrevious}>
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
          <PopoverTrigger asChild>
            <ButtonGroupText
              asChild
              className="cursor-pointer whitespace-nowrap bg-background hover:bg-accent"
            >
              <button type="button">{getFormattedDate()}</button>
            </ButtonGroupText>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="center">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={handleCalendarSelect}
              defaultMonth={selectedDate}
              weekStartsOn={1}
            />
          </PopoverContent>
        </Popover>

        <Button variant="outline" size="sm" onClick={navigateNext}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </ButtonGroup>

      <Button variant="outline" size="sm" onClick={goToToday}>
        {getTodayLabel()}
      </Button>
    </div>
  );
}
